import { api } from './client'

/**
 * Blocco 1 / Card D (REQ-125-001, 134-001, 136-001, 168-001, 200-001) — client dei crediti attesi (`ReceivablesController`,
 * `RevenueRulesController`). Le forme JSON sono quelle pinnate nel brief. L'incassato e' derivato dal server (somma delle
 * allocazioni riconciliate), mai scritto da qui. Senza `CanSeeRealAmounts` le chiavi degli importi sono ASSENTI (ne' null
 * ne' 0): per questo i campi importo sono opzionali e la UI mostra "—".
 */

export const RECEIVABLE_STATUSES = [
  'Previsto', 'Maturato', 'DaIncassare', 'ParzialmenteIncassato', 'Incassato', 'Contestato', 'Scaduto', 'Stornato', 'Annullato',
] as const
export type ReceivableStatus = typeof RECEIVABLE_STATUSES[number]

export const RECEIVABLE_STATUS_LABELS: Record<ReceivableStatus, string> = {
  Previsto: 'Previsto',
  Maturato: 'Maturato',
  DaIncassare: 'Da incassare',
  ParzialmenteIncassato: 'Parzialmente incassato',
  Incassato: 'Incassato',
  Contestato: 'Contestato',
  Scaduto: 'Scaduto',
  Stornato: 'Stornato',
  Annullato: 'Annullato',
}

export interface Drill {
  href: string
}

export interface ReceivableRow {
  id: number
  contractId: number
  operatorId: number | null
  customerId: number | null
  productId: number | null
  agentUserId: number | null
  status: ReceivableStatus
  probability: number
  expectedAmount?: number
  accruedAmount?: number | null
  collectedAmount?: number
  residualAmount?: number
  expectedCollectionDate: string | null
  daysOverdue: number
  invoiceId: number | null
  drill: Drill | null
}

export interface ReceivableList {
  items: ReceivableRow[]
  total: number
}

export interface ReceivableFilters {
  status?: string
  operatorId?: string
  departmentId?: string
  productId?: string
  agentId?: string
  from?: string
  to?: string
  page?: number
  pageSize?: number
}

export interface ReceivableOperatorSummary {
  operatorId: number
  residual?: number
  overdue?: number
  avgDelayDays: number | null
}

export interface ReceivableSummary {
  total?: number
  overdue?: number
  dueSoon30?: number
  disputed?: number
  collectedMonth?: number
  aging?: { d0_30: number; d31_60: number; d61_90: number; d90p: number }
  byOperator?: ReceivableOperatorSummary[]
}

export interface ReceivableSyncResult {
  created: number
  updated: number
  unchanged: number
  skipped: number
}

export interface ReceivablePatch {
  expectedCollectionDate?: string | null
  probability?: number
  notes?: string | null
  invoiceId?: number | null
  documentId?: number | null
}

export type RevenueBasis = 'company_revenue' | 'fixed' | 'pct_expected'

export const REVENUE_BASIS_LABELS: Record<RevenueBasis, string> = {
  company_revenue: 'Ricavo Equal del prodotto',
  fixed: 'Importo fisso',
  pct_expected: '% dell’atteso',
}

export interface RevenueRule {
  id: number
  operatorId: number | null
  productId: number | null
  triggerStatus: string
  basis: RevenueBasis
  fixedAmount: number | null
  percentage: number | null
  collectionDelayDays: number
  validFrom: string
  validTo: string | null
  version: number
  reason: string
  updatedBy: number | null
}

export interface RevenueRuleBody {
  operatorId: number | null
  productId: number | null
  triggerStatus: string
  basis: RevenueBasis
  fixedAmount: number | null
  percentage: number | null
  collectionDelayDays: number
  validFrom: string
  reason: string
}

/** Risposta dell'anteprima: la forma non e' pinnata, si mostrano le coppie chiave/valore cosi' come arrivano. */
export type RevenueRulePreview = Record<string, unknown>

export interface ReceivableSettings {
  probabilities: Record<string, number>
  overdueAlertDays: number
  prudentMinProbability: number
  realisticLookbackMonths: number
}

function qs(params: object): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(params) as [string, unknown][]) {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v))
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

const json = (body: unknown) => JSON.stringify(body)

export const receivablesApi = {
  list: (f: ReceivableFilters) => api<ReceivableList>(`/api/finance/receivables${qs(f)}`),
  summary: (f: Omit<ReceivableFilters, 'page' | 'pageSize'>) => api<ReceivableSummary>(`/api/finance/receivables/summary${qs(f)}`),
  patch: (id: number, body: ReceivablePatch) =>
    api<ReceivableRow>(`/api/finance/receivables/${id}`, { method: 'PATCH', body: json(body) }),
  dispute: (id: number, reason: string) =>
    api<ReceivableRow>(`/api/finance/receivables/${id}/dispute`, { method: 'POST', body: json({ reason }) }),
  resolve: (id: number) => api<ReceivableRow>(`/api/finance/receivables/${id}/resolve`, { method: 'POST' }),
  cancel: (id: number, reason: string) =>
    api<ReceivableRow>(`/api/finance/receivables/${id}/cancel`, { method: 'POST', body: json({ reason }) }),
  sync: () => api<ReceivableSyncResult>('/api/finance/receivables/sync', { method: 'POST' }),
  settings: () => api<ReceivableSettings>('/api/finance/receivables/settings'),
  saveSettings: (body: ReceivableSettings) =>
    api<ReceivableSettings>('/api/finance/receivables/settings', { method: 'PUT', body: json(body) }),
}

export const revenueRulesApi = {
  /** Accetta sia un array sia `{ items }`: la forma della lista non e' pinnata. */
  list: async (): Promise<RevenueRule[]> => {
    const r = await api<RevenueRule[] | { items: RevenueRule[] }>('/api/finance/revenue-rules')
    return Array.isArray(r) ? r : r.items
  },
  create: (body: RevenueRuleBody) => api<RevenueRule>('/api/finance/revenue-rules', { method: 'POST', body: json(body) }),
  newVersion: (id: number, body: RevenueRuleBody) =>
    api<RevenueRule>(`/api/finance/revenue-rules/${id}/versions`, { method: 'POST', body: json(body) }),
  preview: (contractId: number) =>
    api<RevenueRulePreview>(`/api/finance/revenue-rules/preview${qs({ contractId })}`),
}
